import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const CTA_URL = "https://manage.scan2ad.com/";

const LINKS = [
    { label: "Home", hash: "#home", testid: "nav-link-home" },
    { label: "How It Works", hash: "#how-it-works", testid: "nav-link-how-it-works" },
    { label: "Gallery", hash: "#examples", testid: "nav-link-examples" },
    {
        label: "Contact",
        href: "https://dolphin-digital.com/contact-us",
        testid: "nav-link-contact",
    },
];

export const scrollToHash = (hash, closeMenu) => (e) => {
    e.preventDefault();
    const el = document.querySelector(hash);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
    else el.scrollIntoView({ behavior: "smooth" });
    if (closeMenu) closeMenu();
};

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [open, setOpen] = useState(false);
    const lastY = useRef(0);

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;
            setScrolled(y > 24);
            if (y > 140 && y > lastY.current) setHidden(true);
            else if (y < lastY.current) setHidden(false);
            lastY.current = y;
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            initial={{ y: "-100%" }}
            animate={{ y: hidden && !open ? "-100%" : "0%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "border-b border-slate-200/70 bg-white/85 shadow-[0_8px_30px_rgba(15,23,42,0.05)] backdrop-blur-xl"
                    : "bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <a
                    href="#home"
                    onClick={scrollToHash("#home")}
                    className="flex items-center gap-3"
                    data-testid="scan2ad-logo"
                >
                    <img
                        src="/assets/scan2ad-logo.webp"
                        alt="Scan2Ad"
                        className="h-12 w-auto sm:h-14"
                    />
                </a>

                <nav className="hidden items-center gap-9 lg:flex">
                    {LINKS.map((link) => (
                        <a
                            key={link.label}
                            href={link.href || link.hash}
                            onClick={link.hash ? scrollToHash(link.hash) : undefined}
                            target={link.href ? "_blank" : undefined}
                            rel={link.href ? "noopener noreferrer" : undefined}
                            data-testid={link.testid}
                            className="group relative text-sm font-semibold text-slate-600 transition-colors duration-200 hover:text-slate-900"
                        >
                            {link.label}
                            <span className="absolute -bottom-1.5 left-0 h-[2px] w-0 rounded-full bg-teal-500 transition-all duration-300 group-hover:w-full" />
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <motion.a
                        href={CTA_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-create-ad-button"
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                        className="hidden items-center gap-2 rounded-full bg-teal-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-teal-600/25 transition-colors duration-200 hover:bg-teal-700 sm:inline-flex"
                    >
                        Launch the App
                        <ArrowUpRight className="h-4 w-4" />
                    </motion.a>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-menu-button"
                        aria-label="Toggle menu"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm ring-1 ring-slate-200 lg:hidden"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="overflow-hidden border-t border-slate-200/70 bg-white/95 backdrop-blur-xl lg:hidden"
                    >
                        <div className="space-y-1 px-6 py-4">
                            {LINKS.map((link) => (
                                <a
                                    key={link.label}
                                    href={link.href || link.hash}
                                    onClick={
                                        link.hash
                                            ? scrollToHash(link.hash, () => setOpen(false))
                                            : () => setOpen(false)
                                    }
                                    target={link.href ? "_blank" : undefined}
                                    rel={link.href ? "noopener noreferrer" : undefined}
                                    className="block rounded-xl px-4 py-3 text-base font-semibold text-slate-700 hover:bg-teal-50"
                                >
                                    {link.label}
                                </a>
                            ))}
                            <a
                                href={CTA_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-teal-600 px-5 py-3 text-sm font-semibold text-white"
                            >
                                Launch the App
                                <ArrowUpRight className="h-4 w-4" />
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
